import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { esModoMarcador, marcasSinPermiso, mismoPatron, ocultarEnlacesA, ocultarMarcas, pantallasSinPermiso, patronDeRuta, quitarOcultos } from './huella-rbac';

/**
 * EL VIGILANTE: aplica en la pantalla lo que se marcó desde «Agregar permisos».
 *
 * Las marcas (GET /api/admin/rbac/marcas) dicen «este botón / esta sección / esta pantalla pide tal permiso».
 * En cada navegación y con un observador del DOM, para las marcas de la ruta actual:
 *  - acción o sección sin permiso  -> el elemento recibe `data-rbac-oculto` (styles.css lo esconde con display:none);
 *  - pantalla sin permiso          -> se saca a la ruta por omisión, igual que IsPermissionGuard.
 *
 * El permiso se pregunta con AuthService.hasPermission tal cual: un usuario sin RBAC (legacy) lo ve todo, como hoy.
 * Esto es comodidad de la interfaz; la seguridad de verdad la sigue poniendo el servidor.
 *
 * Reglas de cuidado:
 *  - Nunca corre en modo marcador (ahí el administrador tiene que ver todo para poder marcarlo).
 *  - Si no hay marcas sin permiso para la ruta actual, no hay observador ni trabajo.
 *  - Si la carga de marcas falla, no se esconde nada y la app sigue normal.
 *  - Se procesa dentro del propio callback del observador (microtarea, antes de pintar): no hay parpadeo.
 *  - El observador corre fuera de la zona de Angular, para que no dispare la detección de cambios en cada mutación.
 */
@Injectable({ providedIn: 'root' })
export class RbacVigilanteService {

  private static readonly REINTENTO_MS = 30_000;
  /** Las marcas se vuelven a pedir al navegar si las que se tienen tienen más de esto (las marcas nuevas llegan sin reiniciar sesión). */
  private static readonly REFRESCO_MS = 60_000;

  private iniciado = false;
  private sesionActiva = false;
  private marcas: any[] = [];
  private pendientes: any[] = []; // marcas de acción/sección de la ruta actual que el usuario no puede ver
  private vedadas: any[] = []; // marcas de tipo pantalla (de cualquier ruta) sin permiso: se esconden sus enlaces
  private cargaFallida = false;
  private ultimoIntento = 0;
  private observador: MutationObserver | null = null;
  private suscripciones = new Subscription();

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: AuthService,
    private zone: NgZone,
  ) { }

  iniciar(): void {
    if (this.iniciado || esModoMarcador(window)) return;
    this.iniciado = true;

    // auth$ es un BehaviorSubject: al suscribirse ya trae el estado de la sesión (restaurada del almacenamiento).
    this.suscripciones.add(this.auth.auth$.subscribe((autenticado: any) => {
      if (autenticado) this.alIniciarSesion();
      else this.alCerrarSesion();
    }));
    this.suscripciones.add(this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe(() => this.alNavegar()));
    // Los permisos llegan (o cambian) después de iniciar sesión: se vuelve a evaluar con los nuevos.
    this.suscripciones.add(combineLatest([this.auth.permissions$, this.auth.hasRbac$])
      .subscribe(() => this.recalcular(true)));
  }

  // ───────────────────────── Sesión y carga ─────────────────────────

  private alIniciarSesion(): void {
    if (this.sesionActiva) return; // auth$ vuelve a emitir true al renovar el token: no se recarga
    this.sesionActiva = true;
    this.cargarMarcas();
  }

  private alCerrarSesion(): void {
    this.sesionActiva = false;
    this.cargaFallida = false;
    this.marcas = [];
    this.pendientes = [];
    this.vedadas = [];
    this.desconectar();
    quitarOcultos(document);
  }

  private cargarMarcas(): void {
    this.cargaFallida = false;
    this.ultimoIntento = Date.now();
    // Es una lectura de fondo: si falla con 401 no debe cerrar la sesión del usuario.
    const headers = new HttpHeaders({ 'X-Sin-Cerrar-Sesion': 'true' });
    this.http.get<any>(`${environment.BASE_LA}/api/admin/rbac/marcas`, { headers }).subscribe({
      next: (resp: any) => {
        if (!this.sesionActiva) return; // se cerró la sesión mientras llegaba la respuesta
        this.marcas = Array.isArray(resp?.data) ? resp.data : [];
        this.recalcular(false);
      },
      error: (err: any) => {
        this.cargaFallida = true;
        if (!this.marcas.length) this.marcas = []; // un refresco que falla conserva las marcas que ya había
        console.warn('[RBAC] Vigilante: no se pudieron cargar las marcas; no se esconde nada.', err?.status);
      },
    });
  }

  private alNavegar(): void {
    // Otra pantalla: lo escondido en la anterior se devuelve a la vista (un elemento que persiste, como el menú o un
    // contenedor del chrome, no debe quedar escondido en las demás) y enseguida se recalcula para la ruta nueva.
    quitarOcultos(document);
    if (this.sesionActiva && this.cargaFallida && Date.now() - this.ultimoIntento > RbacVigilanteService.REINTENTO_MS) {
      this.cargarMarcas(); // un reintento tranquilo, cuando el usuario ya está navegando
      return;
    }
    this.recalcular(false);
    // Antes las marcas se leían solo al iniciar sesión: una marca agregada después no se aplicaba hasta volver a entrar.
    if (this.sesionActiva && Date.now() - this.ultimoIntento > RbacVigilanteService.REFRESCO_MS) this.cargarMarcas();
  }

  /** Vuelve a leer las marcas ahora (por ejemplo, justo después de guardar en «Agregar permisos»). */
  recargarMarcas(): void {
    if (this.sesionActiva && !esModoMarcador(window)) this.cargarMarcas();
  }

  // ───────────────────────── Aplicar ─────────────────────────

  /** Ve qué marcas de la ruta actual no puede ver el usuario y las aplica. `reiniciar`: antes deshace lo escondido. */
  private recalcular(reiniciar: boolean): void {
    if (!this.marcas.length) {
      this.pendientes = [];
      this.vedadas = [];
      this.desconectar();
      return;
    }
    if (reiniciar) quitarOcultos(document); // los permisos cambiaron: lo que antes se escondió puede volver a verse

    const url = this.router.url;
    // Se compara contra el PATRÓN de la ruta activa (`/console/merchants/:code`), el mismo formato que guarda la
    // pestaña «Agregar permisos»; así un `:param` no se come a una ruta literal hermana (`/console/merchants/nuevo`).
    const patron = patronDeRuta(this.router.routerState.snapshot.root);
    const sinPermiso = marcasSinPermiso(this.marcas, url, (codigo: string) => this.auth.hasPermission(codigo), patron);

    if (sinPermiso.some((m: any) => m.tipo === 'pantalla') && this.sacarDePantalla(url)) {
      this.pendientes = [];
      this.desconectar();
      return;
    }

    this.pendientes = sinPermiso.filter((m: any) => m.tipo !== 'pantalla');
    // Pantallas (de cualquier ruta) que el usuario no puede abrir: se esconden los enlaces que llevan a ellas.
    this.vedadas = pantallasSinPermiso(this.marcas, (codigo: string) => this.auth.hasPermission(codigo));
    if (!this.pendientes.length && !this.vedadas.length) {
      this.desconectar();
      return;
    }
    ocultarMarcas(document, this.pendientes);
    ocultarEnlacesA(document, this.vedadas);
    this.conectar();
  }

  /** La pantalla entera sin permiso: a la ruta por omisión, como IsPermissionGuard. Devuelve si navegó. */
  private sacarDePantalla(url: string): boolean {
    const destino = this.auth.getDefaultRoute();
    if (mismoPatron(destino, url)) return false; // ya está ahí: no dar vueltas en círculo
    this.zone.run(() => this.router.navigateByUrl(destino, { replaceUrl: true }));
    return true;
  }

  private conectar(): void {
    if (this.observador) return;
    this.zone.runOutsideAngular(() => {
      this.observador = new MutationObserver((registros: MutationRecord[]) => this.alMutar(registros));
      // Solo hijos: poner el atributo no vuelve a disparar el observador.
      this.observador.observe(document.body, { childList: true, subtree: true });
    });
  }

  private desconectar(): void {
    if (!this.observador) return;
    this.observador.disconnect();
    this.observador = null;
  }

  private alMutar(registros: MutationRecord[]): void {
    if (!this.pendientes.length && !this.vedadas.length) return;
    // Angular deja comentarios y nodos de texto a cada vuelta: solo importa si entró algún elemento.
    let entroElemento = false;
    for (const r of registros) {
      for (let i = 0; i < r.addedNodes.length; i++) {
        if (r.addedNodes[i].nodeType === 1) { entroElemento = true; break; }
      }
      if (entroElemento) break;
    }
    if (entroElemento) {
      ocultarMarcas(document, this.pendientes);
      ocultarEnlacesA(document, this.vedadas);
    }
  }
}
